import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';
import React from 'react';
import { DropdownButton, Dropdown } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

const LANGUAGE_CODE_ENGLISH = 'eng';
const LANGUAGE_CODE_SPANISH = 'spa';
const LANGUAGE_CODE_DEFAULT = 'default';

/**
 * Sorts language options by translated label, with English and Spanish pinned to the top.
 * Optional "default" option stays first if present. Remaining languages are sorted
 * alphabetically by their translated display label (locale-aware).
 * @param {Array<{ code: string, value?: string, label?: string }>} options - Language options
 * @param {function({ id: string }): string} formatMessage - react-intl formatMessage
 * @returns {Array} New array with same items in sorted order
 */
export function sortLanguageOptions(options, formatMessage) {
    const getLabel = (lang) => {
        const id = lang.text || `ssap.input.option.language_${lang.code}`;
        return formatMessage({ id });
    };

    const rank = (code) => {
        if (code === LANGUAGE_CODE_DEFAULT) return -2;
        if (code === LANGUAGE_CODE_ENGLISH) return -1;
        if (code === LANGUAGE_CODE_SPANISH) return 0;
        return 1;
    };

    //skip sorting if only default languag options
    if (options.length <= 3) { 
        return options;
    }

    return options.sort((a, b) => {
        const rankA = rank(a.code);
        const rankB = rank(b.code);
        if (rankA !== rankB) return rankA - rankB;
        return getLabel(a).localeCompare(getLabel(b), undefined, { sensitivity: 'base' });
    });
}

const CustomDropdownButton = styled(DropdownButton)`
    .dropdown-toggle::after {
        display: none !important; /* This is to hide the caret */
    }

    .gi-button__outline {
        @media screen and (max-width: 640px) {
            width: 100%;
            margin-top: 10px;
            margin-bottom: 10px;
        }
    }
`;

const LanguageMenu = (props) => {

    const {
        onFieldChange, spokenOrWrittenLanguage, languageOptions = []
    } = props;

    const intl = useIntl();
    const rawOptions = languageOptions.length > 0 ? languageOptions : DROPDOWN_OPTIONS.LANGUAGES;
    const sortedOptions = sortLanguageOptions(rawOptions, intl.formatMessage);

    const handleSelect = (eventKey) => {
        onFieldChange(spokenOrWrittenLanguage, eventKey);
    };

    return (
        <CustomDropdownButton
            id={`${spokenOrWrittenLanguage}_dropdown_button`}
            data-testid={`${spokenOrWrittenLanguage}_dropdown`}
            title={<FormattedMessage id="ssap.button.change" />}
            bsPrefix="gi-button gi-button__outline"
            onSelect={handleSelect}
        >
            {sortedOptions.map(language => (
                <Dropdown.Item
                    key={language.code}
                    eventKey={language.label ?? language.value}
                    data-testid={`${spokenOrWrittenLanguage}_option_${language.code}`}
                    as="div"
                    className="usa-button gi-button gi-button__subtle text-center"
                >
                    <FormattedMessage id={`ssap.input.option.language_${language.code}`} />
                </Dropdown.Item>
            ))}
        </CustomDropdownButton>
    );
};

export default LanguageMenu;
